import styled from '@emotion/styled';
import is from '@sindresorhus/is';
import { type FunctionComponent, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { useTranslate } from '~/shared/context/use-translate';
import { availableI18nLanguages } from '~/shared/internationalization';
import type { TranslationKey } from '~/shared/internationalization/translation';
import { Button } from '~/ui/core/button/Button';
import { Dialog } from '~/ui/core/dialog/Dialog';
import { Input } from '~/ui/core/input/Input';
import { RadioGroup, RadioGroupItem } from '~/ui/core/radio-group/RadioGroup';
import { Select, SelectItem } from '~/ui/core/select/Select';
import { Spinner } from '~/ui/core/spinner/Spinner';

import { useOrganizationManagementOptions } from '../../../../position-management/access/api/use-position-access-grants';
import { RoleCheckboxGroup } from './RoleCheckboxGroup';
import { DialogActions, DialogForm, FormField, FormLabel } from './styles';
import type { InvitationMethod, InviteTeamMemberFormData } from './types';

type InviteTeamMemberDialogProps = {
    readonly allowedRoles?: readonly string[];
    readonly availableMethods?: readonly InvitationMethod[];
    readonly optionsAreLoading?: boolean;
    readonly onClose: () => void;
    readonly onInvite: (data: InviteTeamMemberFormData) => void;
    readonly open: boolean;
    readonly title?: string;
};

const Content = styled(Dialog.Content)({
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '28rem',
    overflow: 'hidden',
    padding: 0,
});

const ScrollableContent = styled.div(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xl,
    overflowY: 'auto',
    padding: `${theme.spacing['3xl']} ${theme.spacing['3xl']} 0`,
}));

const ActionsWrapper = styled.div(({ theme }) => ({
    padding: theme.spacing['3xl'],
}));

type FormValues = {
    readonly email: string;
    readonly firstName: string;
    readonly language: string;
    readonly lastName: string;
    readonly method: InvitationMethod;
    readonly roles: readonly string[] | null;
};

const NO_ROLES: readonly string[] = [];

const languageEntries = Object.entries(availableI18nLanguages) as ReadonlyArray<
    readonly [string, { readonly code: string; readonly name: string }]
>;

const useDialogTitle = (title: string | undefined): string => {
    const translate = useTranslate();

    return title ?? translate('pages.company-settings.users-and-roles.invite-dialog.title');
};

export const InviteTeamMemberDialog: FunctionComponent<InviteTeamMemberDialogProps> = ({
    allowedRoles,
    availableMethods,
    optionsAreLoading = false,
    onClose,
    onInvite,
    open,
    title,
}) => {
    const translate = useTranslate();
    const {
        applicationState: { customization },
    } = useApplicationContext();
    const ssoRoleManagementInApp = customization
        .get('usersConfiguration')
        .get('ssoRoleManagementInApp')
        .unwrapOr(false);
    const { i18n } = useTranslation();
    const currentLanguage = i18n.resolvedLanguage ?? 'en-US';
    const defaultMethod = availableMethods?.[0] ?? 'EMAIL';
    const dialogTitle = useDialogTitle(title);
    const organizationOptions = useOrganizationManagementOptions({ enabled: open });
    const offeredRoles = organizationOptions.data?.invitableRoles ?? NO_ROLES;
    const invitableRoles = is.undefined(allowedRoles)
        ? offeredRoles
        : offeredRoles.filter((role) => allowedRoles.includes(role));
    const defaultRoles = invitableRoles.filter((role) => role === 'user');

    const { control, handleSubmit, reset, setValue, watch } = useForm<FormValues>({
        defaultValues: {
            email: '',
            firstName: '',
            language: currentLanguage,
            lastName: '',
            method: defaultMethod,
            roles: null,
        },
    });

    const selectedMethod = watch('method');
    const selectedRoles = watch('roles') ?? defaultRoles;
    const showMethodSelector = availableMethods !== undefined && availableMethods.length > 1;
    const sendsRoles = selectedMethod !== 'SSO' || ssoRoleManagementInApp;
    const showRoleSelector = sendsRoles && is.nonEmptyArray(invitableRoles);

    useEffect(() => {
        if (open) {
            const method = availableMethods?.[0];
            if (is.nonEmptyString(method)) {
                setValue('method', method);
            }
        }
    }, [open, availableMethods, setValue]);

    const handleFormSubmit = handleSubmit((data) => {
        const roles = data.roles ?? defaultRoles;

        onInvite({
            email: data.email,
            firstName: data.firstName,
            inviteLanguage: data.language,
            lastName: data.lastName,
            method: data.method,
            roles: sendsRoles ? (is.nonEmptyArray(roles) ? roles : ['user']) : [],
        });
        reset();
        onClose();
    });

    const handleOpenChange = (isOpen: boolean): void => {
        if (!isOpen) {
            reset();
            onClose();
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <Dialog.Portal>
                <Dialog.DarkBackdrop />
                <Content>
                    <DialogForm className="relative" onSubmit={handleFormSubmit}>
                        {optionsAreLoading && (
                            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80">
                                <Spinner className="h-6 w-6" />
                            </div>
                        )}
                        <ScrollableContent>
                            <Dialog.Title>{dialogTitle}</Dialog.Title>
                            <FormField>
                                <FormLabel htmlFor="firstName">
                                    {translate('pages.company-settings.users-and-roles.invite-dialog.first-name')} *
                                </FormLabel>
                                <Controller
                                    control={control}
                                    name="firstName"
                                    render={({ field }) => (
                                        <Input
                                            id="firstName"
                                            name={field.name}
                                            placeholder={translate(
                                                'pages.company-settings.users-and-roles.invite-dialog.first-name-placeholder',
                                            )}
                                            type="text"
                                            value={field.value}
                                            onBlur={field.onBlur}
                                            onChange={field.onChange}
                                        />
                                    )}
                                    rules={{ required: true }}
                                />
                            </FormField>
                            <FormField>
                                <FormLabel htmlFor="lastName">
                                    {translate('pages.company-settings.users-and-roles.invite-dialog.last-name')} *
                                </FormLabel>
                                <Controller
                                    control={control}
                                    name="lastName"
                                    render={({ field }) => (
                                        <Input
                                            id="lastName"
                                            name={field.name}
                                            placeholder={translate(
                                                'pages.company-settings.users-and-roles.invite-dialog.last-name-placeholder',
                                            )}
                                            type="text"
                                            value={field.value}
                                            onBlur={field.onBlur}
                                            onChange={field.onChange}
                                        />
                                    )}
                                    rules={{ required: true }}
                                />
                            </FormField>
                            <FormField>
                                <FormLabel htmlFor="email">
                                    {translate('pages.company-settings.users-and-roles.invite-dialog.email')} *
                                </FormLabel>
                                <Controller
                                    control={control}
                                    name="email"
                                    render={({ field }) => (
                                        <Input
                                            id="email"
                                            name={field.name}
                                            placeholder={translate(
                                                'pages.company-settings.users-and-roles.invite-dialog.email-placeholder',
                                            )}
                                            type="email"
                                            value={field.value}
                                            onBlur={field.onBlur}
                                            onChange={field.onChange}
                                        />
                                    )}
                                    rules={{ required: true }}
                                />
                            </FormField>
                            {showMethodSelector && (
                                <FormField>
                                    <FormLabel>
                                        {translate('pages.company-settings.users-and-roles.invite-dialog.method')}
                                    </FormLabel>
                                    <Controller
                                        control={control}
                                        name="method"
                                        render={({ field }) => (
                                            <RadioGroup value={field.value} onValueChange={field.onChange}>
                                                {availableMethods.map((method) => {
                                                    const prefix =
                                                        'pages.company-settings.users-and-roles.invite-dialog';
                                                    const methodKey = `${prefix}.method-${method.toLowerCase()}`;

                                                    return (
                                                        <RadioGroupItem
                                                            key={method}
                                                            label={translate(methodKey as TranslationKey)}
                                                            value={method}
                                                        />
                                                    );
                                                })}
                                            </RadioGroup>
                                        )}
                                    />
                                </FormField>
                            )}
                            {showRoleSelector && (
                                <FormField>
                                    <Controller
                                        control={control}
                                        name="roles"
                                        render={({ field }) => (
                                            <RoleCheckboxGroup
                                                label={translate(
                                                    'pages.company-settings.users-and-roles.invite-dialog.roles',
                                                )}
                                                roles={invitableRoles}
                                                value={field.value ?? defaultRoles}
                                                onChange={field.onChange}
                                            />
                                        )}
                                    />
                                </FormField>
                            )}
                            <FormField>
                                <FormLabel>
                                    {translate('pages.company-settings.users-and-roles.invite-dialog.language')}
                                </FormLabel>
                                <Controller
                                    control={control}
                                    name="language"
                                    render={({ field }) => (
                                        <Select value={field.value} onValueChange={field.onChange}>
                                            {languageEntries.map(([key, config]) => (
                                                <SelectItem key={key} value={config.code}>
                                                    {config.name}
                                                </SelectItem>
                                            ))}
                                        </Select>
                                    )}
                                />
                            </FormField>
                        </ScrollableContent>
                        <ActionsWrapper>
                            <DialogActions>
                                <Button type="button" variant="tertiary" onClick={() => handleOpenChange(false)}>
                                    {translate('common.cancel')}
                                </Button>
                                <Button
                                    disabled={
                                        optionsAreLoading ||
                                        organizationOptions.isLoading ||
                                        (showRoleSelector && selectedRoles.length === 0)
                                    }
                                    type="submit"
                                    variant="primary"
                                >
                                    {translate('pages.company-settings.users-and-roles.invite-dialog.invite-button')}
                                </Button>
                            </DialogActions>
                        </ActionsWrapper>
                    </DialogForm>
                </Content>
            </Dialog.Portal>
        </Dialog>
    );
};
